/**
 * @file overlayStacking.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-19
 * @modified 2026-09-19
 * @description Stacking order for modal overlays: the last one opened — or the
 *              last one touched — is the one on top.
 *
 *              Every overlay in the app carried the SAME z-index (9998), so
 *              which of two modals drew on top came down to DOM order, i.e. the
 *              order React happened to append the portals in. That is right
 *              often enough to look deliberate and wrong often enough to be
 *              reported: a PO opened from inside another modal could land
 *              underneath it. Worse, a couple of hand-rolled overlays had picked
 *              their own numbers (LinkPosDialog at 10001) to force their way
 *              above whatever spawned them — which then buried anything opened
 *              from THEM, one layer further down the same hole.
 *
 *              Installed once with a MutationObserver rather than added as a
 *              hook every modal calls: there are around twenty overlays in the
 *              app and only a handful go through components/Modal.js. The rest
 *              are hand-rolled portals, and a rule each has to opt into is a
 *              rule the twenty-first will not have. Same reasoning — and the
 *              same install point — as the backdrop-click guard in index.js.
 *
 *              The body's own children are watched, NOT the whole subtree:
 *              every overlay in the app is a createPortal into document.body
 *              and therefore a direct child of it, so a subtree observer would
 *              wake on every row of every re-rendering table to learn nothing.
 *              An overlay rendered inline instead of portalled would be missed
 *              here — but it would also be trapped inside its ancestors'
 *              stacking context, where no z-index of ours could lift it above a
 *              body-level modal anyway. Portal it and it is covered.
 *
 *              The band is 9000–9900, deliberately BELOW the dialog layer at
 *              9999+ (ConfirmDialog, Alert, the context menus, the date
 *              picker's popper). A confirm raised from a modal has to sit above
 *              it however deep the modal stack has grown, so those keep their
 *              fixed numbers and stay out of this.
 */

/** Everything that stacks as a modal. Mirrors index.js's OVERLAY_CLASSES. */
export const OVERLAY_SELECTOR = '.modal-overlay, .link-pos-dialog-overlay';

/** The foot of the band, and the ceiling that keeps it clear of 9999. */
const BASE = 9000;
const MAX_STEPS = 900;

/** Live overlays, back-most first. */
let order = [];
let installed = false;

/**
 * Re-stamp the whole band.
 *
 * Positions are assigned from the array index rather than from an
 * ever-climbing counter, so the numbers are bounded by how many overlays are
 * actually open — four modals deep can never creep up into the dialog layer.
 */
const applyOrder = () => {
  // A modal that closed between mutations leaves a detached node behind; drop
  // it here rather than trusting every removal to have been observed.
  order = order.filter((el) => el.isConnected);
  order.forEach((el, i) => {
    el.style.zIndex = String(BASE + Math.min(i, MAX_STEPS));
  });
};

/** The overlays a mutated node either is or contains. */
const overlaysIn = (node) => {
  if (!(node instanceof Element)) return [];
  const self = node.matches(OVERLAY_SELECTOR) ? [node] : [];
  return self.concat(Array.from(node.querySelectorAll(OVERLAY_SELECTOR)));
};

/**
 * Bring one overlay to the front. A no-op when it is already there, so the
 * common case of clicking about inside the topmost modal restamps nothing.
 */
export function raiseOverlay(el) {
  if (!el || order[order.length - 1] === el) return;
  const at = order.indexOf(el);
  if (at === -1) return;
  order.splice(at, 1);
  order.push(el);
  applyOrder();
}

/**
 * The given elements sorted back-most first — the order they are actually
 * PAINTED in, which since this file exists is no longer the order they appear
 * in the DOM. Anything asking "which modal is on top" (the global ESC handler,
 * for one) has to ask this rather than take the last match.
 *
 * Array.prototype.sort is stable, so overlays sharing a z-index — a dialog
 * layer at a fixed 9999, say — keep their DOM order relative to each other.
 */
export function inStackOrder(elements) {
  const depth = (el) => {
    const inline = Number(el.style.zIndex);
    if (el.style.zIndex !== '' && Number.isFinite(inline)) return inline;
    const computed = Number(window.getComputedStyle(el).zIndex);
    return Number.isFinite(computed) ? computed : 0;
  };
  return Array.from(elements).sort((a, b) => depth(a) - depth(b));
}

/** Call once, before anything renders. */
export function installOverlayStacking() {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  const observer = new MutationObserver((records) => {
    let changed = false;
    for (const record of records) {
      for (const node of record.addedNodes) {
        for (const el of overlaysIn(node)) {
          if (!order.includes(el)) {
            order.push(el);
            changed = true;
          }
        }
      }
      for (const node of record.removedNodes) {
        for (const el of overlaysIn(node)) {
          const at = order.indexOf(el);
          if (at !== -1) {
            order.splice(at, 1);
            changed = true;
          }
        }
      }
    }
    if (changed) applyOrder();
  });

  observer.observe(document.body, { childList: true });

  /*
   * Raise on pointerdown, in the capture phase.
   *
   * pointerdown rather than click because the raise should happen as the finger
   * lands, before whatever the click goes on to do — and because index.js's
   * backdrop guard cancels click on an overlay outright, so a click-phase
   * listener would never hear about a tap on the backdrop.
   *
   * In practice this only matters where a lower modal is reachable at all: a
   * full-screen backdrop swallows everything aimed at what is underneath it, so
   * for most of the app "last clicked" and "last opened" are the same modal.
   * It is the cheap half of the rule and it covers the cases that aren't.
   */
  document.addEventListener('pointerdown', (e) => {
    const el = e.target instanceof Element ? e.target.closest(OVERLAY_SELECTOR) : null;
    if (el) raiseOverlay(el);
  }, true);
}
